/**
 * PacingProgressBar
 *
 * Three-segment bar used by both the Marketing Lead Pacing Report (multi-location)
 * and the Forecast page Sales Department Analysis card (single-location).
 *
 * Segments (left to right):
 *   • leads today  (blue-700)
 *   • at current pace (blue-200) — additional leads projected by EOM
 *   • gap (rose-200) — shortfall vs leads needed at the rolling close rate
 *
 * Status pill (Ahead/On/Behind/No goal) is exported separately so callers can
 * place it independently of the bar.
 */

import React from 'react';

const numFmt = new Intl.NumberFormat('en-US');

export type PaceStatusKey = 'ahead' | 'on-pace' | 'behind' | 'no-goal';

export interface PaceStatus {
  key: PaceStatusKey;
  label: string;
  pillClass: string;
}

/** Map incrementalLeadsNeeded (positive = behind, negative = ahead) to a status. */
export function paceStatus(incremental: number | null): PaceStatus {
  if (incremental === null) {
    return { key: 'no-goal', label: 'No goal set', pillClass: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
  if (incremental > 0) {
    return { key: 'behind', label: 'Behind pace', pillClass: 'bg-rose-50 text-rose-700 border-rose-200' };
  }
  if (incremental === 0) {
    return { key: 'on-pace', label: 'On pace', pillClass: 'bg-amber-50 text-amber-700 border-amber-200' };
  }
  return { key: 'ahead', label: 'Ahead of pace', pillClass: 'bg-green-50 text-green-700 border-green-200' };
}

export interface SegmentWidths {
  today: number;
  atPace: number;
  gap: number;
  todayCum: number;
  paceCum: number;
}

/**
 * Compute proportional segment widths, then bump any non-zero segment up to a
 * floor so its inline label stays readable, then renormalize to 100%. Without
 * this, a tiny gap (e.g. 37 on a 3,700 total) renders ~1% wide and clips the
 * "−37 gap" label. Small visual distortion of the larger segments is acceptable
 * — the actual values are labeled inside each segment.
 *
 * `minPct` lets the Forecast card use a slightly larger floor (narrower bar
 * needs more breathing room for the labels).
 */
export function computeSegmentWidths(
  mtdLeads: number,
  atPace: number,
  gap: number,
  minPct = 9,
): SegmentWidths {
  const totalVal = Math.max(1, mtdLeads + atPace + gap);
  const raw = [
    { value: mtdLeads, rawPct: (mtdLeads / totalVal) * 100 },
    { value: atPace, rawPct: (atPace / totalVal) * 100 },
    { value: gap, rawPct: (gap / totalVal) * 100 },
  ];
  const bumped = raw.map(s => ({ ...s, pct: s.value > 0 ? Math.max(s.rawPct, minPct) : 0 }));
  const sumBumped = bumped.reduce((s, x) => s + x.pct, 0) || 1;
  const today = (bumped[0].pct / sumBumped) * 100;
  const atPacePct = (bumped[1].pct / sumBumped) * 100;
  const gapPct = (bumped[2].pct / sumBumped) * 100;
  return {
    today,
    atPace: atPacePct,
    gap: gapPct,
    todayCum: today,
    paceCum: today + atPacePct,
  };
}

export interface PacingProgressBarProps {
  mtdLeads: number;
  atPace: number;
  gap: number;
  /** Bar height. Default "tall" (h-14) suits the multi-loc report;
   *  "short" (h-10) is denser for the Forecast card's narrow column. */
  size?: 'tall' | 'short';
  /** Minimum width floor for any non-zero segment. Default 9. */
  minSegmentPct?: number;
}

export const PacingProgressBar: React.FC<PacingProgressBarProps> = ({
  mtdLeads,
  atPace,
  gap,
  size = 'tall',
  minSegmentPct,
}) => {
  const widths = computeSegmentWidths(mtdLeads, atPace, gap, minSegmentPct);
  const heightClass = size === 'short' ? 'h-10' : 'h-14';
  const valueTextClass = size === 'short' ? 'text-xs leading-tight' : 'text-sm leading-tight';
  return (
    <div className={`flex ${heightClass} rounded-md overflow-hidden bg-gray-100 border border-gray-200`}>
      {mtdLeads > 0 && (
        <div
          className="bg-blue-700 text-white flex flex-col items-center justify-center px-2 overflow-hidden"
          style={{ width: `${widths.today}%` }}
          title={`${numFmt.format(mtdLeads)} leads MTD`}
        >
          <div className={`${valueTextClass} font-bold`}>{numFmt.format(mtdLeads)}</div>
          <div className="text-[9px] uppercase tracking-wide opacity-90">today</div>
        </div>
      )}
      {atPace > 0 && (
        <div
          className="bg-blue-200 text-blue-900 flex flex-col items-center justify-center px-2 overflow-hidden"
          style={{ width: `${widths.atPace}%` }}
          title={`+${numFmt.format(atPace)} additional leads projected at current pace`}
        >
          <div className={`${valueTextClass} font-bold`}>+{numFmt.format(atPace)}</div>
          <div className="text-[9px] uppercase tracking-wide opacity-80">at pace</div>
        </div>
      )}
      {gap > 0 && (
        <div
          className="bg-rose-200 text-rose-900 flex flex-col items-center justify-center px-2 overflow-hidden"
          style={{ width: `${widths.gap}%` }}
          title={`Shortfall of ${numFmt.format(gap)} leads to hit goal at the rolling close rate`}
        >
          <div className={`${valueTextClass} font-bold`}>−{numFmt.format(gap)}</div>
          <div className="text-[9px] uppercase tracking-wide opacity-80">gap</div>
        </div>
      )}
    </div>
  );
};
